// The text of each niche's ad page (/comecar/:nicho): title, subtitle and the example Keepo drawn on the phone.
// Portuguese, as the ad pages are whatever the browser's language (English in nicheCopy.en.ts, for `?lng=en`); not in the
// translation files (the generic page, which /register shows in the person's language, is: pages.adSignup.generic).
// The page's key is the niche's `key` (see niches.ts).
import { ExampleKeepoContent } from "./ExampleKeepo";

export type AdNicheCopy = { title: string; subtitle: string; example: ExampleKeepoContent };

export const AD_NICHE_COPY: Record<string, AdNicheCopy> = {
  psicologos: {
    title: "Seu site de psicologia em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: abordagem, atendimento e contato num só link, sem escrever nada do zero.",
    example: {
      name: "Ana Lima",
      bio: "Psicóloga · online e presencial",
      buttons: ["Agendar sessão", "WhatsApp", "Como funciona a terapia"],
      aboutTitle: "Sobre mim",
      about: "Atendo adolescentes e adultos com escuta cuidadosa, no seu tempo.",
      headline: "Um espaço seguro para cuidar de você",
      section: "Atendimentos",
      items: [
        {
          name: "Terapia individual",
          detail: "50 min",
        },
        {
          name: "Terapia de casal",
          detail: "80 min",
        },
      ],
      quote: "Me senti acolhida desde a primeira sessão.",
      quoteBy: "Paciente",
    },
  },
  nutricionistas: {
    title: "Seu site de nutrição em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: especialidades, consulta e agendamento num só lugar.",
    example: {
      name: "Júlia Ramos",
      bio: "Nutricionista esportiva",
      buttons: ["Agendar consulta", "WhatsApp", "Planos"],
      aboutTitle: "Sobre mim",
      about: "Planos alimentares feitos para a sua rotina e os seus treinos.",
      headline: "Comer bem sem complicar a rotina",
      section: "Consultas",
      items: [
        {
          name: "Primeira consulta",
          detail: "60 min",
        },
        {
          name: "Retorno",
          detail: "30 min",
        },
      ],
      quote: "Aprendi a comer melhor sem sofrer.",
      quoteBy: "Paciente",
    },
  },
  dentistas: {
    title: "Seu site de odontologia em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: tratamentos, convênios, endereço e agendamento numa página que abre rápido no celular.",
    example: {
      name: "Clínica Sorriso Leve",
      bio: "Odontologia para toda a família",
      buttons: ["Agendar avaliação", "Tratamentos", "Como chegar"],
      aboutTitle: "A clínica",
      about: "Do primeiro dentinho ao clareamento, com atendimento sem pressa.",
      headline: "Sorrisos cuidados com calma e carinho",
      section: "Tratamentos",
      items: [
        {
          name: "Limpeza",
          detail: "a partir de R$ 180",
        },
        {
          name: "Clareamento",
          detail: "a partir de R$ 690",
        },
      ],
      quote: "Meu filho saiu da consulta sorrindo.",
      quoteBy: "Mãe do Pedro",
    },
  },
  fisioterapeutas: {
    title: "Seu site de fisioterapia em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: atendimentos, estúdio e contato sempre à mão para quem quer começar.",
    example: {
      name: "Rafael Souza",
      bio: "Fisioterapia e pilates",
      buttons: ["Agendar avaliação", "WhatsApp", "Onde atendo"],
      aboutTitle: "Sobre mim",
      about: "Reabilitação e prevenção de lesões, com atenção a cada movimento.",
      headline: "Volte a se mexer sem dor",
      section: "Atendimentos",
      items: [
        {
          name: "Fisioterapia",
          detail: "50 min",
        },
        {
          name: "Pilates",
          detail: "55 min",
        },
      ],
      quote: "Voltei a correr depois de anos.",
      quoteBy: "Paciente",
    },
  },
  personalTrainers: {
    title: "Seu site de personal trainer em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: planos, resultados e contratação num link só, sem responder direct por direct.",
    example: {
      name: "Bruno Alves",
      bio: "Personal trainer",
      buttons: ["Quero treinar", "WhatsApp", "Planos"],
      aboutTitle: "Sobre mim",
      about: "Treinos montados para o seu objetivo, presenciais ou online.",
      headline: "Seu treino, do seu jeito",
      section: "Planos",
      items: [
        {
          name: "Presencial",
          detail: "3x por semana",
        },
        {
          name: "Online",
          detail: "treino + ajustes",
        },
      ],
      quote: "Em três meses mudei de vida.",
      quoteBy: "Aluno",
    },
  },
  esteticistas: {
    title: "Seu site de estética em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: procedimentos, valores e agendamento numa página com a cara da sua marca.",
    example: {
      name: "Studio Pele Bela",
      bio: "Estética facial e corporal",
      buttons: ["Agendar horário", "Procedimentos", "Valores"],
      aboutTitle: "O studio",
      about: "Procedimentos com técnica e cuidado, num espaço feito para relaxar.",
      headline: "Sua pele, cuidada de verdade",
      section: "Procedimentos",
      items: [
        {
          name: "Limpeza de pele",
          detail: "R$ 150",
        },
        {
          name: "Drenagem",
          detail: "R$ 120",
        },
      ],
      quote: "Saí renovada.",
      quoteBy: "Cliente",
    },
  },
  advogados: {
    title: "Seu site de advocacia em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: áreas de atuação, formação e contato numa página sóbria e informativa.",
    example: {
      name: "Carvalho Advocacia",
      bio: "Direito de família e trabalhista",
      buttons: ["Falar com um advogado", "Áreas de atuação", "Escritório"],
      aboutTitle: "O escritório",
      about: "Orientação clara e próxima, do primeiro contato à solução.",
      headline: "Seus direitos explicados com clareza",
      section: "Áreas",
      items: [
        {
          name: "Família",
          detail: "divórcio e guarda",
        },
        {
          name: "Trabalhista",
          detail: "rescisão e horas",
        },
      ],
      quote: "Me explicaram tudo sem juridiquês.",
      quoteBy: "Cliente",
    },
  },
  medicos: {
    title: "Seu site de medicina em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: especialidades, convênios e agendamento numa página clara para o paciente.",
    example: {
      name: "Paulo Mendes",
      bio: "Clínico geral",
      buttons: ["Agendar consulta", "Convênios", "Consultório"],
      aboutTitle: "Sobre mim",
      about: "Atendimento atento, com tempo para ouvir e explicar.",
      headline: "Cuidado com tempo para ouvir",
      section: "Consultas",
      items: [
        {
          name: "Consulta",
          detail: "40 min",
        },
        {
          name: "Check-up",
          detail: "exames e retorno",
        },
      ],
      quote: "Finalmente um médico que escuta.",
      quoteBy: "Paciente",
    },
  },
  terapeutas: {
    title: "Seu site de terapias em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: suas práticas e sessões apresentadas com calma e clareza.",
    example: {
      name: "Lua Terapias",
      bio: "Reiki e terapias integrativas",
      buttons: ["Agendar sessão", "WhatsApp", "Terapias"],
      aboutTitle: "Sobre mim",
      about: "Práticas para equilibrar corpo e mente, no seu ritmo.",
      headline: "Equilíbrio para corpo e mente",
      section: "Terapias",
      items: [
        {
          name: "Reiki",
          detail: "60 min",
        },
        {
          name: "Aromaterapia",
          detail: "45 min",
        },
      ],
      quote: "Saio de cada sessão mais leve.",
      quoteBy: "Cliente",
    },
  },
  profissionaisDeSaude: {
    title: "Seu site de saúde em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: serviços, locais de atendimento e contato num só link.",
    example: {
      name: "Espaço Bem Viver",
      bio: "Saúde integrada",
      buttons: ["Agendar atendimento", "Especialidades", "Como chegar"],
      aboutTitle: "O espaço",
      about: "Profissionais de várias áreas cuidando de você num só lugar.",
      headline: "Sua saúde em boas mãos",
      section: "Especialidades",
      items: [
        {
          name: "Nutrição",
          detail: "consulta 60 min",
        },
        {
          name: "Psicologia",
          detail: "sessão 50 min",
        },
      ],
      quote: "Atendimento atencioso do início ao fim.",
      quoteBy: "Paciente",
    },
  },
  coachesEMentores: {
    title: "Seu site de mentoria em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: método, programas e resultados com o caminho para a conversa sempre visível.",
    example: {
      name: "Carla Nunes",
      bio: "Mentora de carreira",
      buttons: ["Agendar conversa", "Programas", "Depoimentos"],
      aboutTitle: "Sobre mim",
      about: "Ajudo profissionais a dar o próximo passo com método e clareza.",
      headline: "Seu próximo passo com clareza",
      section: "Programas",
      items: [
        {
          name: "Mentoria individual",
          detail: "8 encontros",
        },
        {
          name: "Grupo",
          detail: "6 semanas",
        },
      ],
      quote: "Saí com um plano de verdade.",
      quoteBy: "Mentorada",
    },
  },
  corretoresDeImoveis: {
    title: "Seu site de imóveis em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: seus imóveis em destaque e o contato de quem quer visitar.",
    example: {
      name: "Lucas Torres",
      bio: "Compra, venda e aluguel",
      buttons: ["Ver imóveis", "WhatsApp", "Avaliar meu imóvel"],
      aboutTitle: "Sobre mim",
      about: "Ajudo você a encontrar o imóvel certo, sem pressa e sem surpresa.",
      headline: "O imóvel certo para você",
      section: "Destaques",
      items: [
        {
          name: "Apto 2 quartos",
          detail: "Centro",
        },
        {
          name: "Casa com quintal",
          detail: "Jardins",
        },
      ],
      quote: "Achei meu apê em duas semanas.",
      quoteBy: "Cliente",
    },
  },
  arquitetos: {
    title: "Seu site de arquitetura em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: projetos, processo e pedidos de orçamento num link elegante.",
    example: {
      name: "Atelier Linha",
      bio: "Arquitetura e interiores",
      buttons: ["Pedir orçamento", "Projetos", "Processo"],
      aboutTitle: "O atelier",
      about: "Projetos que unem beleza e funcionalidade, do conceito à obra.",
      headline: "Espaços com a sua cara",
      section: "Serviços",
      items: [
        {
          name: "Interiores",
          detail: "por ambiente",
        },
        {
          name: "Reforma completa",
          detail: "sob consulta",
        },
      ],
      quote: "Nossa casa ficou do jeito que sonhamos.",
      quoteBy: "Cliente",
    },
  },
  fotografos: {
    title: "Seu site de fotógrafo em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: portfólio, pacotes e pedidos de orçamento a um toque da bio.",
    example: {
      name: "Marina Luz",
      bio: "Fotografia de família e eventos",
      buttons: ["Pedir orçamento", "Portfólio", "Pacotes"],
      aboutTitle: "Sobre mim",
      about: "Fotos naturais, que guardam o momento como ele foi.",
      headline: "Momentos que ficam para sempre",
      section: "Pacotes",
      items: [
        {
          name: "Ensaio família",
          detail: "1h + 30 fotos",
        },
        {
          name: "Evento",
          detail: "a partir de 3h",
        },
      ],
      quote: "As fotos ficaram lindas!",
      quoteBy: "Cliente",
    },
  },
  negociosDeAlimentacao: {
    title: "Seu site de restaurante em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: cardápio com foto e preço e pedido direto no WhatsApp.",
    example: {
      name: "Cantina da Vila",
      bio: "Comida caseira e delivery",
      buttons: ["Fazer pedido", "Cardápio", "Horários"],
      aboutTitle: "A cantina",
      about: "Comida feita na hora, com tempero de casa.",
      headline: "Comida de casa, do jeito certo",
      section: "Cardápio",
      items: [
        {
          name: "Prato do dia",
          detail: "R$ 32",
        },
        {
          name: "Lasanha",
          detail: "R$ 45",
        },
      ],
      quote: "Melhor marmita do bairro.",
      quoteBy: "Cliente",
    },
  },
  lojas: {
    title: "Seu site de loja em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: novidades e canais de venda reunidos para levar o cliente à compra.",
    example: {
      name: "Ateliê Bia",
      bio: "Moda feminina",
      buttons: ["Comprar no WhatsApp", "Novidades", "Endereço"],
      aboutTitle: "A loja",
      about: "Peças escolhidas a dedo, com atendimento próximo.",
      headline: "Novidades toda semana",
      section: "Destaques",
      items: [
        {
          name: "Vestido midi",
          detail: "R$ 189",
        },
        {
          name: "Camisa de linho",
          detail: "R$ 149",
        },
      ],
      quote: "Atendimento impecável.",
      quoteBy: "Cliente",
    },
  },
  contadores: {
    title: "Seu site de contabilidade em 1 minuto",
    subtitle:
      "A IA lê seu Instagram e monta seu site: seus serviços em linguagem simples e contato organizado de novos clientes.",
    example: {
      name: "Prisma Contabilidade",
      bio: "Contabilidade para pequenas empresas",
      buttons: ["Falar com um contador", "Serviços", "Abrir empresa"],
      aboutTitle: "O escritório",
      about: "Cuidamos dos números para você cuidar do seu negócio.",
      headline: "Sua empresa em dia, sem dor de cabeça",
      section: "Serviços",
      items: [
        {
          name: "Abertura de empresa",
          detail: "a partir de R$ 490",
        },
        {
          name: "Mensalidade",
          detail: "a partir de R$ 290",
        },
      ],
      quote: "Finalmente entendi meus impostos.",
      quoteBy: "Cliente",
    },
  },
  tatuadores: {
    title: "Seu site de estúdio de tatuagem em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: portfólio, orçamento e agenda num só link.",
    example: {
      name: "Estúdio Traço Fino",
      bio: "Fineline e blackwork",
      buttons: ["Pedir orçamento", "Portfólio", "Agenda do mês"],
      aboutTitle: "O estúdio",
      about: "Cada projeto é desenhado do zero, a partir da sua ideia.",
      headline: "Sua ideia, desenhada só para você",
      section: "Estilos",
      items: [
        {
          name: "Fineline",
          detail: "a partir de R$ 300",
        },
        {
          name: "Blackwork",
          detail: "a partir de R$ 450",
        },
      ],
      quote: "Ficou exatamente como eu imaginei.",
      quoteBy: "Cliente",
    },
  },
  petShops: {
    title: "Seu site de pet shop em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: serviços, valores por porte e agendamento para o tutor.",
    example: {
      name: "Pet Amigo",
      bio: "Banho, tosa e veterinário",
      buttons: ["Agendar banho", "Serviços", "Como chegar"],
      aboutTitle: "O pet shop",
      about: "Cuidado e carinho para o seu melhor amigo.",
      headline: "Seu pet bem cuidado e feliz",
      section: "Serviços",
      items: [
        {
          name: "Banho",
          detail: "a partir de R$ 50",
        },
        {
          name: "Banho e tosa",
          detail: "a partir de R$ 80",
        },
      ],
      quote: "O Thor volta sempre cheiroso.",
      quoteBy: "Tutora do Thor",
    },
  },
  igrejas: {
    title: "Seu site de igreja em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: cultos, endereço e transmissões num link fácil de compartilhar.",
    example: {
      name: "Comunidade Esperança",
      bio: "Igreja cristã",
      buttons: ["Horários dos cultos", "Assistir ao vivo", "Como chegar"],
      aboutTitle: "Quem somos",
      about: "Uma comunidade de portas abertas para você e sua família.",
      headline: "Venha como você está",
      section: "Cultos",
      items: [
        {
          name: "Domingo",
          detail: "10h e 19h",
        },
        {
          name: "Quarta",
          detail: "20h",
        },
      ],
      quote: "Encontrei uma família aqui.",
      quoteBy: "Membro",
    },
  },
  artistas: {
    title: "Seu site em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: lançamentos, shows e contato para contratação num link só.",
    example: {
      name: "Dani Som",
      bio: "Cantora e compositora",
      buttons: ["Ouvir agora", "Agenda de shows", "Contratar"],
      aboutTitle: "Sobre mim",
      about: "Música brasileira com novos arranjos, ao vivo e nas plataformas.",
      headline: "Música para ouvir e dançar",
      section: "Próximos shows",
      items: [
        {
          name: "Sexta",
          detail: "Bar do Centro",
        },
        {
          name: "Sábado",
          detail: "Festival da Vila",
        },
      ],
      quote: "Show incrível do começo ao fim.",
      quoteBy: "Fã",
    },
  },
  freelancers: {
    title: "Seu site de freelancer em 1 minuto",
    subtitle: "A IA lê seu Instagram e monta seu site: portfólio, serviços e contato num cartão de visitas digital.",
    example: {
      name: "Léo Design",
      bio: "Designer e ilustrador",
      buttons: ["Pedir orçamento", "Portfólio", "Serviços"],
      aboutTitle: "Sobre mim",
      about: "Identidade visual e ilustração para marcas que querem se destacar.",
      headline: "Design que conta a sua história",
      section: "Serviços",
      items: [
        {
          name: "Identidade visual",
          detail: "a partir de R$ 1.200",
        },
        {
          name: "Ilustração",
          detail: "sob consulta",
        },
      ],
      quote: "Entendeu minha marca de primeira.",
      quoteBy: "Cliente",
    },
  },
};
